import type { EncryptedEnvelope } from './types';

// Helper utilities for ArrayBuffer <-> Base64
export function bufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Derives an AES-GCM Key Encryption Key (KEK) from a string passphrase using PBKDF2
 */
export async function deriveKeyFromPassphrase(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    true,
    ['wrapKey', 'unwrapKey', 'encrypt', 'decrypt']
  );
}

/**
 * Encrypts a payload into an EncryptedEnvelope using a freshly generated DEK.
 * The DEK is wrapped with the provided Key Encryption Key (KEK).
 */
export async function encryptEnvelope(
  plaintext: Uint8Array,
  kekOrPassphrase: CryptoKey | string,
  keyId = 'primary'
): Promise<EncryptedEnvelope> {
  // 1. Resolve KEK
  let kek: CryptoKey;
  let salt: Uint8Array | undefined;
  if (typeof kekOrPassphrase === 'string') {
    salt = crypto.getRandomValues(new Uint8Array(16));
    kek = await deriveKeyFromPassphrase(kekOrPassphrase, salt);
  } else {
    kek = kekOrPassphrase;
  }

  // 2. Generate random DEK (Data Encryption Key)
  const dek = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  // 3. Encrypt the plaintext with DEK and random 12-byte IV
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    dek,
    plaintext as BufferSource
  );

  // 4. Wrap the DEK with KEK
  const wrapIv = crypto.getRandomValues(new Uint8Array(12));
  const wrappedDekBuffer = await crypto.subtle.wrapKey(
    'raw',
    dek,
    kek,
    { name: 'AES-GCM', iv: wrapIv }
  );

  // Combine wrapIv + (optional salt) + wrappedDek into a single transport string
  const prefix = salt ? `${bufferToBase64(salt)}:${bufferToBase64(wrapIv)}:` : `${bufferToBase64(wrapIv)}:`;
  const wrappedDekStr = prefix + bufferToBase64(wrappedDekBuffer);

  return {
    crypto_version: 1,
    algorithm: 'AES-GCM-256',
    key_id: keyId,
    iv: bufferToBase64(iv),
    wrapped_dek: wrappedDekStr,
    ciphertext: bufferToBase64(ciphertextBuffer),
    tag_length: 128,
    created_at: new Date().toISOString(),
  };
}

/**
 * Decrypts an EncryptedEnvelope back to original plaintext Uint8Array
 */
export async function decryptEnvelope(
  envelope: EncryptedEnvelope,
  kekOrPassphrase: CryptoKey | string
): Promise<Uint8Array> {
  if (envelope.crypto_version !== 1 || envelope.algorithm !== 'AES-GCM-256') {
    throw new Error(`Unsupported envelope version or algorithm: ${envelope.crypto_version} / ${envelope.algorithm}`);
  }

  // 1. Parse wrapped DEK components
  const parts = envelope.wrapped_dek.split(':');
  let kek: CryptoKey;
  let wrapIv: Uint8Array;
  let wrappedDekBytes: Uint8Array;

  if (parts.length === 3) {
    // Has salt (passphrase derived)
    const salt = base64ToBuffer(parts[0]);
    wrapIv = base64ToBuffer(parts[1]);
    wrappedDekBytes = base64ToBuffer(parts[2]);
    if (typeof kekOrPassphrase !== 'string') {
      throw new Error('Envelope requires a passphrase key to derive KEK');
    }
    kek = await deriveKeyFromPassphrase(kekOrPassphrase, salt);
  } else if (parts.length === 2) {
    wrapIv = base64ToBuffer(parts[0]);
    wrappedDekBytes = base64ToBuffer(parts[1]);
    if (typeof kekOrPassphrase === 'string') {
      throw new Error('Envelope was wrapped with a CryptoKey, not a passphrase');
    }
    kek = kekOrPassphrase;
  } else {
    throw new Error('Malformed wrapped_dek format');
  }

  // 2. Unwrap DEK
  const dek = await crypto.subtle.unwrapKey(
    'raw',
    wrappedDekBytes as BufferSource,
    kek,
    { name: 'AES-GCM', iv: wrapIv as BufferSource },
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );

  // 3. Decrypt ciphertext
  const iv = base64ToBuffer(envelope.iv);
  const ciphertext = base64ToBuffer(envelope.ciphertext);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    dek,
    ciphertext as BufferSource
  );

  return new Uint8Array(decryptedBuffer);
}
